import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { isFeatureEnabled } from '@/config/featureFlags';
import { logConnMode } from '@/analytics/connModeLogger';
import { 
  Wifi, 
  WifiOff, 
  Zap
} from 'lucide-react';
import { Mode } from '@/connection/detect';

const getConnectionStatus = (mode: Mode, isManual: boolean) => {
  if (mode === 'offline') {
    return { icon: '⚠️', text: 'No connection', color: 'text-red-600' };
  }
  
  const connectionTypes = {
    'full': { icon: '✅', text: 'Connected (Fast)', color: 'text-green-600' },
    'balanced': { icon: '✅', text: 'Connected (4G)', color: 'text-green-600' }, 
    'lite': { icon: '⚠️', text: 'Weak connection (3G)', color: 'text-yellow-600' }
  };
  
  return connectionTypes[mode];
};

const getModeIcon = (mode: Mode) => {
  switch (mode) {
    case 'full': return '🌾';
    case 'balanced': return '🚜'; 
    case 'lite': return '🌍';
    case 'offline': return '📡';
  }
};

const getModeDescription = (mode: Mode) => {
  switch (mode) {
    case 'full': return 'High-quality images & videos, smooth animations, auto-play content';
    case 'balanced': return 'Optimized images, selective video loading, reduced animations';
    case 'lite': return 'Compressed images, minimal animations, no auto-play, data & battery saving (Auto-enabled for 2G/3G or Data Saver)';
    case 'offline': return 'Cached content only, essential features available';
  }
};

export const ConnectionModeSelector: React.FC = () => {
  const mode = useConnectionMode();
  const [isManualOverride, setIsManualOverride] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'auto' | Mode>('auto');

  if (!isFeatureEnabled('CONNECTION_AWARE_EXPERIENCE')) {
    return null;
  }

  // Check if manual override is active
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const manualMode = localStorage.getItem('mode') as Mode | null;
      setIsManualOverride(manualMode !== null);
      setSelectedMode(manualMode || 'auto');
    }
  }, []);

  const handleModeChange = (value: string) => {
    setSelectedMode(value as 'auto' | Mode);
  };

  const applyChanges = () => {
    if (typeof window === 'undefined') return;
    
    if (selectedMode === 'auto') {
      localStorage.removeItem('mode');
    } else {
      localStorage.setItem('mode', selectedMode);
      
      if (import.meta.env.PROD) {
        logConnMode(selectedMode as Mode);
      }
    }
    
    window.location.reload();
  };

  const connectionStatus = getConnectionStatus(mode, isManualOverride);
  const hasChanges = selectedMode !== (isManualOverride ? localStorage.getItem('mode') || 'auto' : 'auto');

  return (
    <Card className="w-full max-w-lg">
      <CardContent className="space-y-6 pt-6">
        <RadioGroup value={selectedMode} onValueChange={handleModeChange}>
          {/* Auto Mode */}
          <div className="p-4 border rounded-lg bg-green-50 border-green-200">
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="auto" id="auto" />
              <div className="flex-1">
                <Label htmlFor="auto" className="font-medium text-base cursor-pointer flex items-center gap-2">
                  ⚡ Auto Mode (Recommended)
                  {selectedMode === 'auto' && <span className="text-green-600 text-sm">✓ Selected</span>}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Automatically detects and remembers your network
                </p>
              </div>
            </div>
          </div>

          {/* Manual Mode */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="font-medium">OR</span>
            </div>
            
            <div className="p-4 border rounded-lg">
              <div className="mb-3">
                <Label className="font-medium text-base flex items-center gap-2">
                  🚜 Manual Mode
                </Label>
                <p className="text-sm text-muted-foreground">Choose your own setting:</p>
              </div>
              
              <div className="space-y-2 ml-4">
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="full" id="full" />
                  <Label htmlFor="full" className="cursor-pointer flex-1">
                    <div className="font-medium">🌾 Full Mode: For Fast Internet Speeds</div>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="lite" id="lite" />
                  <Label htmlFor="lite" className="cursor-pointer flex-1">
                    <div className="font-medium">🌍 Lite Mode: For rural areas with low speeds</div>
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </RadioGroup>

        {/* Apply Button */}
        {hasChanges && (
          <Button onClick={applyChanges} className="w-full">
            Apply Changes
          </Button>
        )}
      </CardContent>
    </Card>
  );
};